import { FC } from "react";
import { upperFirst } from "lodash";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { ServicesType } from "@/domain/models/job";
import { useTranslator } from "@/i18n/useTranslator";

import { DsDataDisplay } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { DriverTaskPicture } from "../interactors";
import { setJunkLoadInProgress } from "../utils/service";

type DriverTaskReviewJunkSectionPropsType = JobServiceTaskBaseType & {
  junkLoadIndex?: number | null;
};

const DriverTaskReviewJunkSection: FC<DriverTaskReviewJunkSectionPropsType> = ({
  jobId,
  serviceId,
  taskId,
  junkLoadIndex,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const { t } = useTranslator();

  const {
    mutate: updateTask,

    variables: updateTaskVariables,
  } = useUpdateTaskMutation();

  const { mutate: updateService } = useUpdateServiceMutation();

  const hasValidJunkLoadIndex =
    junkLoadIndex !== undefined && junkLoadIndex !== null;

  const goBackTo = (driverFlowStatus: TaskDriverStatusEnum) => {
    const servicesParams: Partial<ServicesType> = {};

    // If driver is performing multiple loads, set the load index to in progress
    if (hasValidJunkLoadIndex && serviceData?.junkLoads) {
      const updatedJunkLoads = setJunkLoadInProgress(
        serviceData,
        junkLoadIndex,
      );

      if (updatedJunkLoads) {
        servicesParams.junkLoads = updatedJunkLoads;
      }
    }

    updateService({
      jobId,
      serviceId,
      driverFlowStatus,
      ...servicesParams,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const data =
    hasValidJunkLoadIndex && serviceData?.junkLoads
      ? serviceData?.junkLoads?.[junkLoadIndex]
      : serviceData;

  const isLastJunkLoadIndex =
    junkLoadIndex === (serviceData?.junkLoads?.length ?? 0) - 1;

  return (
    <>
      <DsDataDisplay
        title="driver_task_review_screen.before_picture"
        dataClassName="flex"
        data={
          <DriverTaskPicture
            field="preJunkRemovalPhoto"
            jobId={jobId}
            serviceId={serviceId}
            junkLoadIndex={junkLoadIndex}
          />
        }
      />
      <DsDataDisplay
        title="driver_task_review_screen.load_size"
        dataClassName="font-bold"
        data={data?.loadSize}
        onEditClick={() => goBackTo(TaskDriverStatusEnum.FILLING_LOAD_SIZE)}
        isLoading={Boolean(
          updateTaskVariables?.driverFlowStatus ===
            TaskDriverStatusEnum.FILLING_LOAD_SIZE,
        )}
      />
      <DsDataDisplay
        title="driver_task_review_screen.location_access"
        dataClassName="font-bold"
        data={upperFirst(data?.locationAccess?.toLowerCase())}
        onEditClick={() =>
          goBackTo(TaskDriverStatusEnum.FILLING_LOCATION_ACCESS)
        }
        isLoading={Boolean(
          updateTaskVariables?.driverFlowStatus ===
            TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
        )}
      />
      <DsDataDisplay
        title="driver_task_review_screen.after_picture"
        dataClassName="flex"
        data={
          <DriverTaskPicture
            field="postJunkRemovalPhoto"
            jobId={jobId}
            serviceId={serviceId}
            junkLoadIndex={junkLoadIndex}
          />
        }
      />
      <DsDataDisplay
        title="driver_task_review_screen.extra_load"
        dataClassName="font-bold"
        data={
          hasValidJunkLoadIndex && !isLastJunkLoadIndex ? t("yes") : t("no")
        }
      />
      {hasValidJunkLoadIndex && (
        <>
          {data?.weightSurcharge && (
            <DsDataDisplay
              title="driver_dump_tasks_review_screen.surcharge"
              dataClassName="font-bold"
              data={data?.weightSurcharge?.toString()}
              onEditClick={() => {
                void goBackTo(TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE);
              }}
              isLoading={Boolean(
                updateTaskVariables?.driverFlowStatus ===
                  TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
              )}
            />
          )}

          {data?.dumpReceiptPhotoUrl && (
            <DsDataDisplay
              title="driver_dump_tasks_review_screen.dump_receipt"
              dataClassName="flex"
              data={
                <DriverTaskPicture
                  field="dumpReceiptPhotoUrl"
                  jobId={jobId}
                  serviceId={serviceId}
                  junkLoadIndex={junkLoadIndex}
                />
              }
            />
          )}
        </>
      )}
    </>
  );
};

export { DriverTaskReviewJunkSection };
